import { useState } from "react";
import Input from "../../../../../common/components/input-service-request/Input";
import Select, { SelectOption } from "../../../../../common/components/input-service-request/Select";
import { DynamicFieldItem } from "../../../../../model/dynamic-forms";
import { Attribute, FieldType, getFileExtensionTypeString } from "../../../../../model/service-request/attribute";
import { formatCurrency } from "../../../../../utils";
import styles from "./AdminDynamicForm.module.scss";

export interface AdminFieldGeneratorProps {
    attributeDefinition: Attribute;
    fieldValueChanged: (value: DynamicFieldItem) => void;
    fieldValueReset?: (value: DynamicFieldItem) => void;
    preselectedFormValue?: DynamicFieldItem;
    error?: string;
    readOnly?: boolean;
}

const makeField = (attr: Attribute, value: any, extra?: Partial<DynamicFieldItem>): DynamicFieldItem => ({
    fieldId: attr.id,
    fieldLabel: attr.label,
    fieldValue: value,
    fieldType: attr.fieldType!,
    fieldPath: `${attr.id}.value`,
    ...extra,
});

const AdminTextField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue, error, readOnly }: AdminFieldGeneratorProps) => (
    <Input
        label={attr.label}
        placeholder={attr.placeholderText ?? ""}
        required={attr.isRequired}
        value={preselectedFormValue?.fieldValue ?? ""}
        maxLength={attr.isLimited ? attr.maxLengthLimit : undefined}
        error={error}
        readOnly={readOnly}
        onChange={(e) => { if (!readOnly) fieldValueChanged(makeField(attr, e.target.value)); }}
    />
);

const AdminNumberField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue, error, readOnly }: AdminFieldGeneratorProps) => (
    <Input
        label={attr.label}
        placeholder={attr.placeholderText ?? ""}
        required={attr.isRequired}
        type="number"
        value={preselectedFormValue?.fieldValue ?? ""}
        min={attr.minNumberValue}
        max={attr.maxNumberValue}
        error={error}
        readOnly={readOnly}
        onChange={(e) => { if (!readOnly) fieldValueChanged(makeField(attr, e.target.value === "" ? "" : Number(e.target.value))); }}
    />
);

const AdminDropdownField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue, error, readOnly }: AdminFieldGeneratorProps) => {
    const isMultiple = attr.isSingleType === false || attr.fieldType === FieldType.MultiSelect;
    const options: SelectOption[] = (attr.childrenOptionDefinitions ?? []).map((o) => ({
        value: o.id ?? o.value,
        label: o.price?.amount ? `${o.value} (${formatCurrency(o.price.amount, { currency: o.price.currency })})` : o.value,
    }));

    if (isMultiple) {
        const selectedValues: string[] = Array.isArray(preselectedFormValue?.selectedChildIds)
            ? preselectedFormValue!.selectedChildIds!
            : Array.isArray(preselectedFormValue?.fieldValue)
            ? preselectedFormValue!.fieldValue
            : [];
        return (
            <Select
                label={attr.label}
                options={options}
                multiple
                values={selectedValues}
                required={attr.isRequired}
                error={error}
                disabled={readOnly}
                onChangeMulti={(opts) => {
                    if (!readOnly) {
                        fieldValueChanged(makeField(attr,
                            opts.length > 0 ? opts.map(o => o.value) : null,
                            opts.length > 0 ? { selectedChildIds: opts.map(o => o.value), dropdownText: opts.map(o => o.label).join(", ") } as any : {}
                        ));
                    }
                }}
            />
        );
    }

    return (
        <Select
            label={attr.label}
            options={options}
            required={attr.isRequired}
            value={preselectedFormValue?.fieldValue ?? ""}
            error={error}
            disabled={readOnly}
            onChange={(opt) => { if (!readOnly) fieldValueChanged(makeField(attr, opt.value, { dropdownText: opt.label, selectedOptionIds: [opt.value] } as any)); }}
        />
    );
};

const AdminConditionalDropdownField = ({ attributeDefinition: attr, fieldValueChanged, fieldValueReset, preselectedFormValue, error, readOnly }: AdminFieldGeneratorProps) => {
    const parentOptions: SelectOption[] = (attr.childrenOptionDefinitions ?? []).map((p) => ({
        value: p.id ?? p.value,
        label: p.price?.amount ? `${p.value} (${formatCurrency(p.price.amount, { currency: p.price.currency })})` : p.value,
    }));

    const findParentForChild = (childValue?: string): string | undefined => {
        if (!childValue) return undefined;
        return (attr.childrenOptionDefinitions ?? []).find(p =>
            (p.childOptions ?? []).some(c => (c.id ?? c.value) === childValue)
        )?.id ?? (attr.childrenOptionDefinitions ?? []).find(p =>
            (p.childOptions ?? []).some(c => (c.id ?? c.value) === childValue)
        )?.value;
    };

    const preselectedChild = preselectedFormValue?.selectedChildId ?? preselectedFormValue?.fieldValue;
    const [selectedParent, setSelectedParent] = useState<string | undefined>(() => findParentForChild(preselectedChild));

    const activeParent = (attr.childrenOptionDefinitions ?? []).find(p => (p.id ?? p.value) === selectedParent);
    const childOptions: SelectOption[] = (activeParent?.childOptions ?? []).map((c) => ({
        value: c.id ?? c.value,
        label: c.price?.amount ? `${c.value} (${formatCurrency(c.price.amount, { currency: c.price.currency })})` : c.value,
    }));

    const selectedChildVal: string = preselectedFormValue?.selectedChildId ?? preselectedFormValue?.fieldValue ?? "";

    return (
        <div className={styles.conditionalDropdownWrapper}>
            <Select
                label={attr.label}
                options={parentOptions}
                value={selectedParent}
                required={attr.isRequired}
                disabled={readOnly}
                onChange={(opt) => {
                    if (!readOnly) {
                        setSelectedParent(opt.value);
                        fieldValueReset?.(makeField(attr, null));
                    }
                }}
            />
            {activeParent && (
                <Select
                    label={parentOptions.find(p => p.value === selectedParent)?.label ?? attr.label}
                    options={childOptions}
                    value={selectedChildVal}
                    required={attr.isRequired}
                    error={error}
                    disabled={readOnly}
                    onChange={(opt) => {
                        if (!readOnly) fieldValueChanged(makeField(attr, opt.value, { selectedChildId: opt.value, dropdownText: opt.label } as any));
                    }}
                />
            )}
        </div>
    );
};

const AdminTextDescriptionField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue, error, readOnly }: AdminFieldGeneratorProps) => (
    <div className={styles.fieldWrapper}>
        <label className={styles.label}>
            {attr.label}
            {attr.isRequired && <span className={styles.required}>*</span>}
        </label>
        <div className={styles.textareaContainer}>
            <textarea
                className={`${styles.textarea} ${error ? styles.textareaError : ""}`}
                placeholder={attr.placeholderText ?? ""}
                value={preselectedFormValue?.fieldValue ?? ""}
                maxLength={attr.isLimited ? attr.maxLengthLimit : undefined}
                readOnly={readOnly}
                onChange={(e) => { if (!readOnly) fieldValueChanged(makeField(attr, e.target.value)); }}
            />
            {error && <span className={styles.error}>{error}</span>}
        </div>
    </div>
);

const AdminBoolField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue }: AdminFieldGeneratorProps) => {
    const checked = preselectedFormValue?.fieldValue ?? attr.defaultState ?? false;

    return (
        <div className={styles.boolRow}>
            <span className={styles.label}>{attr.label}</span>
            <button
                type="button"
                className={`${styles.toggle} ${checked ? styles.toggleActive : ""}`}
                onClick={() => fieldValueChanged(makeField(attr, !checked))}
            >
                <span className={styles.toggleThumb} />
            </button>
        </div>
    );
};

const AdminFileField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue }: AdminFieldGeneratorProps) => {
    const accept = (attr.fileExtensionTypes ?? [])
        .map(ext => `.${getFileExtensionTypeString(ext).toLowerCase()}`)
        .join(",") || undefined;

    return (
        <div className={styles.fieldWrapper}>
            <label className={styles.label}>
                {attr.label}
                {attr.isRequired && <span className={styles.required}>*</span>}
            </label>
            <label className={styles.fileLabel}>
                <input
                    type="file"
                    accept={accept}
                    className={styles.fileInput}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) fieldValueChanged(makeField(attr, file, { fileFileName: file.name }));
                    }}
                />
                <span className={styles.fileText}>
                    {preselectedFormValue?.fileFileName ?? "Choose file"}
                </span>
            </label>
        </div>
    );
};

const AdminDateTimeField = ({ attributeDefinition: attr, fieldValueChanged, preselectedFormValue, error, readOnly }: AdminFieldGeneratorProps) => (
    <Input
        label={attr.label}
        placeholder=""
        required={attr.isRequired}
        type={attr.isIncludeTimeInformation ? "datetime-local" : "date"}
        value={preselectedFormValue?.fieldValue ?? ""}
        error={error}
        readOnly={readOnly}
        onChange={(e) => { if (!readOnly) fieldValueChanged(makeField(attr, e.target.value)); }}
    />
);

export const AdminFieldGenerator = (props: AdminFieldGeneratorProps) => {
    switch (props.attributeDefinition.fieldType) {
        case FieldType.Text:
            return <AdminTextField {...props} />;
        case FieldType.Number:
            return <AdminNumberField {...props} />;
        case FieldType.Bool:
            return <AdminBoolField {...props} />;
        case FieldType.Dropdown:
        case FieldType.SingleSelect:
        case FieldType.ChildDropdown:
        case FieldType.MultiSelect:
            return <AdminDropdownField {...props} />;
        case FieldType.ConditionalDropdown:
            return <AdminConditionalDropdownField {...props} />;
        case FieldType.DateTime:
            return <AdminDateTimeField {...props} />;
        case FieldType.TextDescription:
            return <AdminTextDescriptionField {...props} />;
        case FieldType.File:
        case FieldType.Image:
            return <AdminFileField {...props} />;
        default:
            return null;
    }
};
